import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { sanitizeArticle } from "@/lib/sanitize";
import { ArrowLeft, Clock, Calendar, MapPin, Share2, MessageCircle, Link2 } from "lucide-react";
import { toast } from "sonner";
import { api, resolveImage, categoryLabel, formatDate, BACKEND_URL } from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { ArticleCard } from "@/components/ArticleCard";
import { ReadingProgress } from "@/components/ReadingProgress";
import { VideoPlayer } from "@/components/VideoPlayer";
import { PhotoGallery } from "@/components/PhotoGallery";
import { ListenButton } from "@/components/ListenButton";
import { LangToggle } from "@/components/LangToggle";
import { useLang, pick } from "@/context/LangContext";

export default function ArticlePage() {
  const { slug } = useParams();
  const { lang } = useLang();
  const [article, setArticle] = useState(null);
  const [related, setRelated] = useState([]);
  const [error, setError] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    setArticle(null);
    setError(false);
    api.get(`/articles/${slug}`)
      .then((r) => {
        setArticle(r.data);
        return api.get("/articles", { params: { category: r.data.category, limit: 4 } });
      })
      .then((r) => setRelated(r.data.filter((a) => a.slug !== slug).slice(0, 3)))
      .catch(() => setError(true));
  }, [slug]);

  useEffect(() => {
    document.title = article ? `${article.title} | Bharat Gatha` : "Bharat Gatha";
    return () => { document.title = "Bharat Gatha | भारत गाथा"; };
  }, [article]);

  const shareUrl = `${BACKEND_URL}/api/share/${slug}`;
  const shareText = article ? `${article.title} — Bharat Gatha` : "";

  const copyLink = async () => {
    await navigator.clipboard.writeText(shareUrl);
    toast.success("Share link copied. It shows the cover image and title when pasted.");
  };

  const nativeShare = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: article.title, text: article.excerpt, url: shareUrl }); } catch (_) { /* cancelled */ }
    } else {
      copyLink();
    }
  };

  if (error) {
    return (
      <div data-testid="article-not-found">
        <Navbar />
        <main className="relative z-10 mx-auto max-w-3xl px-5 py-32 text-center">
          <p className="font-devanagari text-5xl text-[var(--saffron)]">क्षमा करें</p>
          <h1 className="mt-4 font-serif text-3xl font-bold text-[var(--chakra-deep)]">Article not found</h1>
          <Link to="/articles" className="mt-6 inline-block text-[var(--chakra)] underline">Back to articles</Link>
        </main>
        <Footer />
      </div>
    );
  }

  if (!article) {
    return (
      <div data-testid="article-loading">
        <Navbar />
        <main className="relative z-10 mx-auto max-w-3xl animate-pulse px-5 py-20">
          <div className="h-5 w-32 rounded bg-white/80" />
          <div className="mt-6 h-14 rounded bg-white/80" />
          <div className="mt-8 aspect-[16/9] rounded-3xl bg-white/80" />
        </main>
      </div>
    );
  }

  const cover = resolveImage(article.cover_image);
  const hasHindi = !!article.content_hi;
  const showHi = lang === "hi" && hasHindi;
  const title = showHi ? article.title_hi || article.title : article.title;
  const excerpt = showHi ? article.excerpt_hi || "" : article.excerpt;
  const content = showHi ? article.content_hi : article.content;

  return (
    <div data-testid="article-page" lang={showHi ? "hi" : "en"}>
      <ReadingProgress />
      <Navbar hideBar />
      <main className="relative z-10">
        <article className="mx-auto max-w-3xl px-5 pt-12 sm:px-8">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <Link to="/articles" data-testid="article-back-link" className="inline-flex items-center gap-2 text-sm font-medium text-stone-500 hover:text-[var(--chakra)]">
              <ArrowLeft className="h-4 w-4" /> All articles
            </Link>
            <LangToggle available={hasHindi} />
          </div>
          {lang === "hi" && !hasHindi && (
            <p className="mt-4 rounded-xl bg-[var(--saffron-tint)] px-4 py-2 text-sm text-[var(--saffron-deep)]" data-testid="hindi-unavailable-note">
              <span className="font-devanagari">इस लेख का हिन्दी संस्करण अभी उपलब्ध नहीं है।</span> Showing the English version.
            </p>
          )}
          <p className="eyebrow mt-8 text-[var(--saffron-deep)]" data-testid="article-category">{categoryLabel(article.category)}</p>
          <h1 className={`mt-3 text-4xl font-bold leading-[1.15] tracking-tight text-[var(--chakra-deep)] sm:text-5xl ${showHi ? "font-devanagari" : "font-serif"}`} data-testid="article-title">{title}</h1>
          {excerpt && <p className="mt-5 text-lg leading-relaxed text-stone-600" data-testid="article-excerpt">{excerpt}</p>}
          <div className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-stone-500" data-testid="article-meta">
            <span className="inline-flex items-center gap-1.5"><Calendar className="h-4 w-4" /> {formatDate(article.published_at)}</span>
            <span className="inline-flex items-center gap-1.5"><Clock className="h-4 w-4" /> {article.read_time} min read</span>
            {article.state && <span className="inline-flex items-center gap-1.5"><MapPin className="h-4 w-4" /> {article.state}</span>}
          </div>
          <div className="mt-5 flex flex-wrap items-center gap-2" data-testid="article-share-bar">
            <span className="mr-1 text-xs font-semibold uppercase tracking-wider text-stone-500">Share</span>
            <a
              href={`https://wa.me/?text=${encodeURIComponent(`${shareText}\n${shareUrl}`)}`}
              target="_blank" rel="noopener noreferrer" data-testid="share-whatsapp-btn"
              className="inline-flex items-center gap-1.5 rounded-full bg-[#25D366] px-3.5 py-1.5 text-xs font-semibold text-white transition-opacity hover:opacity-90"
            >
              <MessageCircle className="h-3.5 w-3.5" /> WhatsApp
            </a>
            <a
              href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`}
              target="_blank" rel="noopener noreferrer" data-testid="share-twitter-btn"
              className="inline-flex items-center gap-1.5 rounded-full bg-stone-900 px-3.5 py-1.5 text-xs font-semibold text-white transition-opacity hover:opacity-90"
            >
              <svg viewBox="0 0 24 24" className="h-3.5 w-3.5 fill-current" aria-hidden="true"><path d="M18.244 2H21.5l-7.5 8.57L22.8 22h-6.9l-5.4-7.06L4.3 22H1.04l8.02-9.17L1.2 2h7.08l4.88 6.45L18.24 2Zm-1.21 18h1.8L7.06 3.9H5.13L17.03 20Z" /></svg>
              X / Twitter
            </a>
            <button onClick={copyLink} data-testid="share-copy-btn" className="inline-flex items-center gap-1.5 rounded-full border border-[var(--line)] bg-white px-3.5 py-1.5 text-xs font-semibold text-stone-700 transition-colors hover:border-[var(--chakra)] hover:text-[var(--chakra)]">
              <Link2 className="h-3.5 w-3.5" /> Copy link
            </button>
            <button onClick={nativeShare} data-testid="article-share-btn" className="inline-flex items-center gap-1.5 rounded-full border border-[var(--line)] bg-white px-3.5 py-1.5 text-xs font-semibold text-stone-700 transition-colors hover:border-[var(--saffron)] hover:text-[var(--saffron-deep)]">
              <Share2 className="h-3.5 w-3.5" /> More
            </button>
          </div>
        </article>

        {cover && (
          <div className="mx-auto mt-10 max-w-5xl px-5 sm:px-8">
            <img src={cover} alt={article.title} className="max-h-[85vh] w-full rounded-3xl bg-[var(--saffron-tint)] object-contain shadow-2xl shadow-orange-100" data-testid="article-cover-image" />
          </div>
        )}

        {article.video_url && (
          <div className="mx-auto mt-8 max-w-5xl px-5 sm:px-8" data-testid="article-video-section">
            <VideoPlayer url={article.video_url} title={article.title} />
          </div>
        )}

        <div className="mx-auto mt-10 max-w-3xl px-5 sm:px-8" data-testid="listen-section">
          <ListenButton slug={article.slug} readTime={article.read_time} lang={showHi ? "hi" : "en"} />
        </div>

        <PhotoGallery images={article.gallery || []} title={article.title} />

        <div className="mx-auto max-w-3xl px-5 py-14 sm:px-8">
          <div className={`article-body ${showHi ? "article-body-hi" : ""}`} data-testid="article-body" dangerouslySetInnerHTML={{ __html: sanitizeArticle(content) }} />

          {article.tags?.length > 0 && (
            <div className="mt-12 flex flex-wrap gap-2" data-testid="article-tags">
              {article.tags.map((t) => (
                <span key={t} className="rounded-full bg-[var(--saffron-tint)] px-3 py-1 text-xs font-medium text-[var(--saffron-deep)]">#{t}</span>
              ))}
            </div>
          )}

          <div className="mt-12 flex items-center gap-4 rounded-2xl border border-[var(--line)] bg-white p-6">
            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-[var(--chakra)] font-devanagari text-2xl text-white">{article.author?.[0] || "A"}</div>
            <div>
              <p className="eyebrow text-[var(--india-green)]">Written by</p>
              <p className="font-serif text-lg font-semibold text-[var(--chakra-deep)]">{article.author}</p>
            </div>
          </div>
        </div>

        {related.length > 0 && (
          <section className="mx-auto max-w-7xl px-5 pb-10 sm:px-8" data-testid="related-articles">
            <h2 className="font-serif text-2xl font-bold text-[var(--chakra-deep)] sm:text-3xl">Keep reading</h2>
            <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {related.map((a, i) => <ArticleCard key={a.id} article={a} index={i} />)}
            </div>
          </section>
        )}
      </main>
      <Footer />
    </div>
  );
}
